/**
 * @license
 * Copyright (c) %FIRST_YEAR% %CURRENT_YEAR%, Oracle and/or its affiliates.
 * Licensed under The Universal Permissive License (UPL), Version 1.0
 * as shown at https://oss.oracle.com/licenses/upl/
 * @ignore
 */

import { getBarGapsInfo } from './barSeriesUtils';
import { getRectCoord } from './barItemUtils';
import { BarItemData, BarSeriesData } from '../barChart.types';
import { Scale, Group, ChartSeriesData } from '#utils/UNSAFE_visTypes/chart';
import { getMixedFreqGroupCenter } from '#utils/PRIVATE_chartUtils/layoutUtils';

/**
 * Returns the base and end coordinate of the bar.
 * @param value The value of the bar item.
 * @param yScale The yscale.
 * @param isStacked Whether the bar chart is stacked or not.
 * @param isLog Whether the y axis is in log scale.
 * @param itemsGaps The gap between data item.
 * @param negCummulativeValue The negative cummulative value of the group.
 * @param posCummulativeValue The positive cummulative value of the group.
 * @returns
 */
export function getBaseAndEndCoord(
  value: number,
  yScale: Scale,
  isStacked: boolean,
  isLog: boolean,
  itemsGaps: number,
  negCummulativeValue: number,
  posCummulativeValue: number
) {
  let baselineCoord;
  let endCoord;
  if (isStacked) {
    baselineCoord = yScale.transform(value < 0 ? negCummulativeValue : posCummulativeValue);
    negCummulativeValue += value < 0 ? value : 0;
    posCummulativeValue += value > 0 ? value : 0;
    endCoord = yScale.transform(value < 0 ? negCummulativeValue : posCummulativeValue);

    baselineCoord += endCoord > baselineCoord ? itemsGaps : -itemsGaps;
  } else {
    baselineCoord = isLog ? yScale.range()[0] : yScale.transform(0);
    endCoord = yScale.transform(value);
  }
  return { baselineCoord, endCoord };
}

export function getBarPosition<K extends string | number, D extends BarItemData<K>>(
  series: BarSeriesData<K, D>[],
  groups: Group[],
  getDataItem: (seriesIndex: number, groupIndex: number) => D | undefined,
  xScale: Scale,
  yScale: Scale,
  y2Scale: Scale,
  hiddenIds: Set<number | string> | undefined,
  dataItemGaps: number,
  axisStepWidth: number,
  averageGroupZ: number,
  barGapRatio: number,
  isLog: boolean,
  isStacked: boolean,
  isRtl: boolean,
  isHoriz: boolean,
  isSplitDualY: boolean,
  getStackGroup?: (s: ChartSeriesData<K, D>) => string | number | undefined,
  timeAxisType?: 'mixedFrequency' | 'enabled' | 'skipGaps'
) {
  const seriesY = series.filter((s) => s.associatedYAxis !== 'y2');
  const seriesY2 = series.filter((s) => s.associatedYAxis === 'y2');
  const numSeriesY = seriesY.length;
  const numSeriesY2 = seriesY2.length;

  const stackGroupOf = (s: ChartSeriesData<K, D>) =>
    (getStackGroup?.(s) ?? 'default') as string | number;
  const yStackGroups = Array.from(new Set(seriesY.map(stackGroupOf)));
  const y2StackGroups = Array.from(new Set(seriesY2.map(stackGroupOf)));
  const numStacksY = yStackGroups.length;
  const numStacksY2 = y2StackGroups.length;
  const cumuls = new Map<string, { pos: number; neg: number }>();

  let yPosCumulativeValue = isLog ? yScale.domain()[0] : 0;
  let yNegCumulativeValue = 0;
  let y2PosCumulativeValue = isLog ? y2Scale.domain()[0] : 0;
  let y2NegCumulativeValue = 0;

  return (seriesIndex: number, groupIndex: number) => {
    const numOfSeries = series.length;

    for (let index = 0; index < numOfSeries; index += 1) {
      const currSeries = series[index];
      const item = getDataItem(index, groupIndex);
      if (!item || hiddenIds?.has(item.id) || (isLog && item.value <= 0)) {
        continue;
      }

      const isCurrY2 = currSeries.associatedYAxis === 'y2';
      const axis = isCurrY2 ? 'y2' : 'y';
      const stackGroup = stackGroupOf(currSeries);
      const key = `${stackGroup}_${axis}`;

      if (!cumuls.has(key)) {
        cumuls.set(key, {
          pos: isLog ? (isCurrY2 ? y2Scale : yScale).domain()[0] : 0,
          neg: 0
        });
      }

      // Grouped-stacked layout: treat each stack group as a side-by-side "series"
      const useGroupedStacks = isStacked && (isCurrY2 ? numStacksY2 : numStacksY) > 1;
      const gapsSeriesIndex = useGroupedStacks
        ? isCurrY2
          ? y2StackGroups.indexOf(stackGroup)
          : yStackGroups.indexOf(stackGroup)
        : isSplitDualY
        ? (isCurrY2 ? seriesY2 : seriesY).findIndex((s) => s.id === currSeries.id)
        : index;

      const gapsNumSeriesY = useGroupedStacks ? numStacksY : numSeriesY;
      const gapsNumSeriesY2 = useGroupedStacks ? numStacksY2 : numSeriesY2;
      // For grouped-stacked, compute side-by-side offsets (not stacked) for bar gaps
      const gapsIsStacked = isStacked && !useGroupedStacks;

      const { barWidth, offset, itemsGaps } = getBarGapsInfo(
        gapsSeriesIndex,
        groupIndex,
        currSeries,
        axisStepWidth,
        averageGroupZ,
        getDataItem,
        barGapRatio,
        isSplitDualY
          ? gapsNumSeriesY
          : useGroupedStacks
          ? isCurrY2
            ? numStacksY2
            : numStacksY
          : series.length,
        isSplitDualY
          ? gapsNumSeriesY2
          : useGroupedStacks
          ? isCurrY2
            ? numStacksY2
            : numStacksY
          : series.length,
        dataItemGaps,
        gapsIsStacked,
        isRtl,
        isHoriz
      );

      if (seriesIndex === index) {
        // Compute group center following BarGroup logic
        let groupCenter: number;
        if (timeAxisType === 'mixedFrequency') {
          groupCenter = getMixedFreqGroupCenter<K, D>(xScale, item);
        } else {
          let xVal: number | string = groupIndex;
          if (timeAxisType) {
            xVal = new Date(groups[groupIndex].id).getTime();
          }
          groupCenter = xScale.transform(xVal as number);
        }

        const x1 = groupCenter + offset;
        const x2 = x1 + barWidth;

        const cumul = cumuls.get(key)!;
        const negCumul = isStacked
          ? cumul.neg
          : isCurrY2
          ? y2NegCumulativeValue
          : yNegCumulativeValue;
        const posCumul = isStacked
          ? cumul.pos
          : isCurrY2
          ? y2PosCumulativeValue
          : yPosCumulativeValue;

        const { baselineCoord, endCoord } = getBaseAndEndCoord(
          item.value,
          isCurrY2 ? y2Scale : yScale,
          isStacked,
          isLog,
          itemsGaps,
          negCumul,
          posCumul
        );

        return getRectCoord(x1, x2, endCoord, baselineCoord, isHoriz);
      }

      // Update cumulatives for subsequent series
      if (isStacked) {
        const cumul = cumuls.get(key)!;
        if (item.value < 0) {
          cumul.neg += item.value;
        } else {
          cumul.pos += item.value;
        }
      } else {
        if (isCurrY2) {
          y2NegCumulativeValue += item.value < 0 ? item.value : 0;
          y2PosCumulativeValue += item.value > 0 ? item.value : 0;
        } else {
          yNegCumulativeValue += item.value < 0 ? item.value : 0;
          yPosCumulativeValue += item.value > 0 ? item.value : 0;
        }
      }
    }
    return;
  };
}

/**
 * Returns the center coordinate of the bar item.
 * @param value
 * @param yScale
 * @param isStacked
 * @param isLog
 * @param dataItemGaps
 * @param negCummValue
 * @param posCummValue
 * @returns
 */
export function getBarCenterCoordinate(
  value: number,
  yScale: Scale,
  isStacked: boolean,
  isLog: boolean,
  dataItemGaps: number,
  negCummValue: number,
  posCummValue: number
) {
  const { baselineCoord, endCoord } = getBaseAndEndCoord(
    value,
    yScale,
    isStacked,
    isLog,
    dataItemGaps,
    negCummValue,
    posCummValue
  );
  return (baselineCoord + endCoord) / 2;
}

export function getSeriesOffsetAndWidth<K extends string | number, D extends BarItemData<K>>(
  axisStepWidth: number,
  averageGroupZ: number,
  series: ChartSeriesData<K, D>[],
  getDataItem: (seriesIndex: number, groupIndex: number) => D | undefined,
  barGapRatio: number,
  dataItemGaps: number,
  isStacked: boolean,
  isRtl: boolean,
  isHoriz: boolean
) {
  const seriesY = series.filter((series) => series.associatedYAxis !== 'y2');
  const numSeriesY = seriesY.length;
  const numSeriesY2 = series.length - numSeriesY;
  return (seriesIndex: number, groupIndex: number) => {
    const { offset, barWidth } = getBarGapsInfo(
      seriesIndex,
      groupIndex,
      series[seriesIndex],
      axisStepWidth,
      averageGroupZ,
      getDataItem,
      barGapRatio,
      numSeriesY,
      numSeriesY2,
      dataItemGaps,
      isStacked,
      isRtl,
      isHoriz
    );
    return { offset, dataWidth: barWidth };
  };
}
